import React from 'react';
import { observer } from 'mobx-react';

import { withNamespaces, WithNamespaces } from 'react-i18next';
// import { SyncStatusType } from '@wikifactory/voxel-ui/SyncStatus/statusMap';

import { routerStore } from 'core';
import { SyncStateEnum } from 'libs/gutClient/types';
import { DriveTable } from '@wikifactory/voxel-ui';
import { SyncRepoAppManager } from '../appManager';
import { ExtendedTreeNode } from '../types';
import { carefulPercent } from '../utils';
import { LocalDriveBanner } from './LocalDriveBanner';
import { navigateToFile } from './navigateUtils';

interface LocalDriveContainerProps extends WithNamespaces {
  syncRepoAppManager: SyncRepoAppManager;
  setSideBarFile: (file: DriveTableDataSource) => void;
}

const resolveParentSyncStatus = (children: DriveTableDataSource[]) => {
  const values = children.map(child => {
    if (child.children) return resolveParentSyncStatus(child.children);
    return child.syncStatus;
  });

  if (values.length === 0) return 'notSynced';
  let folderSyncStatus = 'synced';

  const firstValue = values[0];
  if (values.some(v => v === 'downloading'))
    folderSyncStatus = 'folderDownloading';
  else if (values.some(v => v === 'syncing')) folderSyncStatus = 'syncing';
  else if (values.every(v => v === firstValue)) folderSyncStatus = firstValue;
  else if (values.some(v => v === 'syncFailed'))
    folderSyncStatus = 'partialSyncFailed';
  else if (values.some(v => v === 'notConnected'))
    folderSyncStatus = 'partially';
  else if (values.some(v => v === 'notSynced')) folderSyncStatus = 'partially';

  return folderSyncStatus;
};

export const LocalDriveContainer = withNamespaces()(
  observer(
    ({ syncRepoAppManager, setSideBarFile }: LocalDriveContainerProps) => {
      const { latestContribution, lockingManager, repoManager } =
        syncRepoAppManager;

      let lastContributionView: LastContributionViewProps = null;

      if (latestContribution) {
        const handleCheckInsClick = () => {
          const historyHeader = document.getElementById('history-header');
          if (!historyHeader) return;

          window.scrollTo({
            top: historyHeader.offsetTop,
            behavior: 'smooth',
          });
        };

        lastContributionView = {
          username: latestContribution.creatorProfile.username,
          avatarName: latestContribution.creatorProfile.fullName,
          title: latestContribution.title,
          description: latestContribution.description,
          contributionDate: latestContribution.dateCreated,
          numContributions: repoManager.contributionCount,

          avatarUrl: latestContribution.creatorProfile.avatar?.url,

          onCheckInsClick: () => {
            handleCheckInsClick();
          },
        };
      }

      const { downloadsProgress } = syncRepoAppManager;

      const nodesToDataSource = (
        node: ExtendedTreeNode,
      ): DriveTableDataSource => {
        let syncStatus = 'notSynced';

        if (syncRepoAppManager.pullingOrSyncingBlobs) {
          syncStatus = 'syncing';
        }

        let progress: number = 0;
        const downloadProgress = downloadsProgress.get(node.hash);
        const nodeStatus = syncRepoAppManager.statusMap.get(node.fullPath);
        if (downloadProgress) {
          syncStatus = 'downloading';
          progress = carefulPercent(
            downloadProgress.downloadedBytes,
            downloadProgress.totalBytes,
          );
        } else if (nodeStatus) {
          if (!nodeStatus.head?.syncStatus.sync) {
            syncStatus = 'notConnected';
          } else if (
            nodeStatus.head?.syncStatus.state === SyncStateEnum.SyncFailed
          ) {
            syncStatus = 'syncFailed';
          } else if (
            nodeStatus.head?.syncStatus.state === SyncStateEnum.NotSynced
          ) {
            syncStatus = 'notSynced';
          } else if (
            nodeStatus.head?.syncStatus.state === SyncStateEnum.SyncSucceeded
          ) {
            syncStatus = 'synced';
          }
        }

        const repoFile =
          repoManager.contributionUpstreamFilesByFilename[node.path];

        const cwdFile = repoManager.repoCwdFilesByFilename[node.path];
        const lastContributionMessage = cwdFile?.reference.origin.title || '';
        const createdBy = cwdFile?.reference.origin.creatorProfile?.username;
        const createdAt = cwdFile?.reference.origin.dateCreated;

        const lockedBy = lockingManager.lockedFilesMap[node.fullPath];
        const fileLocked = !!lockedBy;

        let dataSource = {
          id: node.fullPath,
          filename: node.path,
          fileType: 'file',
          lastContributionMessage,
          lastModified: repoFile?.lastUpdated,
          selection: node.selection,
          progress,
          syncStatus,
          fileLocked,
          lockedBy: lockedBy || '—',
          isRemoteLock: !!lockingManager.remoteLockedPathsMap[node.fullPath],
          revision: repoFile?.repoRevision,

          // used in FileSideBar
          createdBy,
          createdAt,

          // not being rendered
          status: 'status',
          maturity: 'maturity',
        } as DriveTableDataSource;

        if (node.tree) {
          const innerRemoteLockedPath = lockingManager.remoteLockedPaths.find(
            path => path.startsWith(node.fullPath),
          );
          const isFolderRemoteLocked = Boolean(innerRemoteLockedPath);
          const folderLocked =
            isFolderRemoteLocked ||
            lockingManager.lockedPaths.some(path =>
              path.startsWith(node.fullPath),
            );
          const folderLockedBy =
            lockingManager.lockedFilesMap[innerRemoteLockedPath];

          const children = node.tree.map(nodesToDataSource);
          const folderSyncStatus = resolveParentSyncStatus(children);

          dataSource = {
            ...dataSource,
            fileType: 'folder',
            children,
            fileLocked: folderLocked,
            isRemoteLock: isFolderRemoteLocked,
            lockedBy: folderLockedBy || '—',
            syncStatus: folderSyncStatus,
          };
        }
        return dataSource;
      };

      const dataSource =
        syncRepoAppManager.localCurrentPathContents.map(nodesToDataSource);

      const navigateToDirectory = (path: string) => {
        const params = {
          ...routerStore.route.params,
          basepath: path || null,
        };

        const routeName = routerStore.route.name;

        const directoryRouteName = routeName.endsWith('.directory')
          ? routeName
          : `${routeName}.directory`;

        const rootDirectoryRouteName = directoryRouteName.substring(
          0,
          directoryRouteName.length - '.directory'.length,
        );

        const toRouteName = params.basepath
          ? directoryRouteName
          : rootDirectoryRouteName;
        routerStore.navigate(toRouteName, params, { preventScrollTop: true });
      };

      const handleBreadcrumbClick = (name: string, index: number) => {
        const path = syncRepoAppManager.breadcrumbs
          .slice(1)
          .slice(0, index)
          .join('/');

        navigateToDirectory(path);
      };

      const handleFileNameClick = (file: DriveTableDataSource) => {
        const { basepath } = routerStore.route.params;
        if (file.fileType === 'folder') {
          let path = file.filename;
          if (basepath) path = `${basepath}/${file.filename}`;
          navigateToDirectory(path);
        } else {
          // navigate to viewer
          const { filename } = file;
          let path = filename;
          if (basepath) path = `${basepath}/${filename}`;
          const hash = syncRepoAppManager.headHash;
          const projectName = repoManager.parent?.project?.name || '';
          navigateToFile(path, filename, hash, projectName);
        }
      };

      const handleLock = () => {
        lockingManager.LockLocalDriveSelection();
      };

      const handleUnlock = () => {
        lockingManager.UnlockLocalDriveSelection();
      };

      const handleSync = () => {
        syncRepoAppManager.PullAndSync(true);
      };

      // const handleDownload = () => {};

      // const handleUpload = () => {};

      const selectedNum = syncRepoAppManager.localTableSelectedRowIds.length;
      const toolbarButtonsDisabled = selectedNum === 0;

      const shouldRenderBreadcrumbs =
        syncRepoAppManager.breadcrumbs?.length > 1;
      const breadcrumbs = shouldRenderBreadcrumbs
        ? syncRepoAppManager.breadcrumbs
        : null;

      const loading =
        dataSource.length === 0 && syncRepoAppManager.loadingLocalDrive;

      return (
        <DriveTable
          title='Local Drive'
          loading={loading}
          banner={<LocalDriveBanner syncRepoAppManager={syncRepoAppManager} />}
          dataSource={dataSource}
          lastContributionView={lastContributionView}
          hasFileDetailsButton
          onFileDetailsClick={(file: DriveTableDataSource) => {
            setSideBarFile(file);
          }}
          toolBar={{
            breadcrumbs,
            onBreadcrumbClick: handleBreadcrumbClick,
            selectedNum,
            onLock: handleLock,
            onUnlock: handleUnlock,
            onSync: handleSync,
            // onDownload: handleDownload,
            // onUpload: handleUpload,
            buttonsDisabled: toolbarButtonsDisabled,
            onOpenLocalFolderClick: () => {
              syncRepoAppManager.openCurrentPath();
            },
          }}
          onSelect={syncRepoAppManager.SelectLocalRow}
          onFileNameClick={(file: DriveTableDataSource) => {
            handleFileNameClick(file);
          }}
        />
      );
    },
  ),
);
